<template>
  <div class="modal fade show" tabindex="-1" role="dialog" style="display: block;">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content tl-modal">
        <div class="modal-header tl-modal-header">
          <h5 class="modal-title">{{ isEditing ? 'Edit Tour' : 'Add New Tour' }}</h5>
          <button type="button" class="close" aria-label="Close" @click="handleClose">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body tl-modal-body">
          <form @submit.prevent="submitForm">
            <div class="form-row">
              <div class="form-group col-md-6 tl-form-group">
                <label class="tl-label">Tour Name <span class="text-danger">*</span></label>
                <input type="text" class="tl-input" v-model="form.tourName" required placeholder="Enter tour name" />
              </div>
              <div class="form-group col-md-6 tl-form-group">
                <label class="tl-label">Cruise Line <span class="text-danger">*</span></label>
                <select class="tl-select" v-model="form.cruiseLine" required>
                  <option v-for="line in cruiseLineOptions" :key="line" :value="line">{{ line }}</option>
                </select>
              </div>
            </div>

            <div class="form-row mt-3">
              <div class="form-group col-md-6 tl-form-group">
                <label class="tl-label">Port <span class="text-danger">*</span></label>
                <select class="tl-select" v-model="selectedPort" required>
                  <option v-for="city in availableCities" :key="city.id" :value="city.id">
                    {{ city.attributes.city }}
                  </option>
                </select>
              </div>
              <div class="form-group col-md-6 tl-form-group">
                <label class="tl-label">Tour Code <span class="text-danger">*</span></label>
                <input type="text" class="tl-input" v-model="form.tourCode" required placeholder="Enter tour code" />
              </div>
            </div>

            <div class="form-row mt-3">
              <div class="form-group col-md-6 tl-form-group">
                <label class="tl-label">Year <span class="text-danger">*</span></label>
                <select class="tl-select" v-model="form.year" required>
                  <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
                </select>
              </div>
              <div class="form-group col-md-6 tl-form-group">
                <label class="tl-label">Duration</label>
                <input type="text" class="tl-input" v-model="form.duration" placeholder="e.g. 4 hrs" />
              </div>
            </div>

            <div class="form-row mt-3">
              <div class="form-group col-md-6 tl-form-group">
                <label class="tl-label">Pax Min <span class="text-danger">*</span></label>
                <input type="number" class="tl-input" v-model="form.paxMin" />
              </div>
              <div class="form-group col-md-6 tl-form-group">
                <label class="tl-label">Pax Max <span class="text-danger">*</span></label>
                <input type="number" class="tl-input" v-model="form.paxMax" required />
              </div>
            </div>

            <div class="form-row mt-3">
              <div class="form-group col-md-6 tl-form-group">
                <label class="tl-label">Timing</label>
                <textarea class="tl-input tl-textarea" v-model="form.timing" rows="2"></textarea>
              </div>
              <div class="form-group col-md-6 tl-form-group">
                <label class="tl-label">Description <span class="text-danger">*</span></label>
                <textarea class="tl-input tl-textarea" v-model="form.description" required rows="2"></textarea>
              </div>
            </div>

            <div class="form-row mt-3">
              <div class="form-group col-md-12 tl-form-group">
                <label class="tl-label">Select City</label>
                <div class="tl-select-group">
                  <select class="tl-select" v-model="selectedCity">
                    <option v-for="city in availableCities" :key="city.id" :value="city.id">
                      {{ city.attributes.city }}
                    </option>
                  </select>
                  <button type="button" class="tl-btn-plus" @click="addCity">+</button>
                </div>

                <div class="mt-3 w-100">
                  <label class="tl-label-sub">Selected Cities</label>
                  <div class="tl-selected-list">
                    <div v-for="city in selectedCities" :key="city" class="tl-selected-item">
                      <span>{{ getCityName(city) }}</span>
                      <button type="button" class="tl-remove-btn" @click="removeCity(city)">&times;</button>
                    </div>
                  </div>
                  <div v-if="cityError" class="tl-error-text">Please select at least one city.</div>
                </div>
              </div>
            </div>

            <div class="tl-form-actions mt-4">
              <button type="submit" class="tl-btn-submit">{{ isEditing ? 'Update' : 'Submit' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
export default {
  props: {
    tour: Object
  },
  data() {
    const startYear = 2024;
    return {
      form: {
        tourName: '',
        cruiseLine: '',
        port: '',
        tourCode: '',
        year: '',
        duration: '',
        paxMin: '',
        paxMax: '',
        timing: '',
        description: '',
      },
      yearOptions: Array.from({ length: 15 }, (v, i) => startYear + i), // Starting from 2024, going forward 20 years

      availableItems: [],
      selectedItems: [],
      availableCities: [],
      selectedCities: [],
      selectedPort: null,
      selectedCity: null,
      selectedItem: null,
      cruiseLineOptions: [
        'Norwegian Cruise Line', 'Princess Cruises', 'Azamara Cruises', 'Celebrity Cruises',
        'Compagnie Francaise de Croisieres', 'Crystal Cruises', 'Cunard', 'Disney Cruise Line',
        'Fred Olsen Cruise Lines', 'Holland America Line', 'Lindblad Expeditions', 'MSC Cruises',
        'Mystic Cruises', 'P&O Cruises', 'Regent Seven Seas Cruises', 'Virgin Voyages',
        'Windstar Cruises', 'Yachts of Seabourn', 'Oceania Cruises', 'Royal Caribbean Cruise Line', 'Vidanta World Elegant',
        'Viking Ocean Cruises', 'Compagnie du Ponant', 'Explora Journeys', 'Atlas Ocean Voyages', 'Ambassador Cruise Line', 'Plantours', 'The Ritz-Carlton Yacht Collection'
      ].sort(),
      cityError: false,
    };
  },
  computed: {
    isEditing() {
      return !!this.tour;
    }
  },
  watch: {
    tour: {
      immediate: true,
      handler(tour) {
        if (tour) {
          this.form = {
            tourName: tour.attributes.tourName,
            cruiseLine: tour.attributes.cruiseLine,
            tourCode: tour.attributes.tourCode,
            year: tour.attributes.year,
            duration: tour.attributes.duration,
            paxMin: tour.attributes.paxMin,
            paxMax: tour.attributes.paxMax,
            timing: tour.attributes.timing,
            description: tour.attributes.description,
          };
          this.selectedPort = tour.attributes.port?.data?.id;
          this.selectedCities = tour.attributes.cities?.data.map(city => city.id);
          this.selectedItems = tour.attributes.item?.data.map(item => item.id);
        }
      }
    }
  },
  async created() {
    await this.fetchItems();
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    async fetchItems() {
      const [itemsResponse, citiesResponse] = await Promise.all([
        this.$xhr.get('/api/servicelondons?populate=*&pagination[pageSize]=1000'),
        this.$xhr.get('/api/citylondons?populate=*&pagination[pageSize]=1000&sort[0]=city:asc')
      ]);

      this.availableItems = itemsResponse.data.data;
      this.availableCities = citiesResponse.data.data.sort((a, b) =>
        a.attributes.city.localeCompare(b.attributes.city)
      );
    },

    addCity() {
      if (this.selectedCity && !this.selectedCities.includes(this.selectedCity)) {
        this.selectedCities.push(this.selectedCity);
        this.selectedCity = null;
        this.cityError = false;
      } else if (!this.selectedCities.length) {
        this.cityError = true;
      }
    },
    removeCity(cityId) {
      this.selectedCities = this.selectedCities.filter(id => id !== cityId);
    },
    addItem() {
      if (this.selectedItem && !this.selectedItems.includes(this.selectedItem)) {
        this.selectedItems.push(this.selectedItem);
        this.selectedItem = null;
      }
    },
    getCityName(id) {
      const city = this.availableCities.find(c => c.id === id);
      return city ? city.attributes.city : 'Unknown City';
    },
    getItemName(id) {
      const item = this.availableItems.find(i => i.id === id);
      return item ? item.attributes.item : 'Unknown Item';
    },
    async submitForm() {
      if (this.selectedCities.length === 0) {
        this.cityError = true;
        return;
      }

      // Strict duplicate check (tourCode + year + cruiseLine)
      const duplicateCheckResponse = await this.$xhr.get(`/api/tourlondons`, {
        params: {
          filters: {
            $and: [
              { tourCode: { $eq: this.form.tourCode } },
              { year: { $eq: this.form.year } },
              { cruiseLine: { $eq: this.form.cruiseLine } }
            ]
          }
        }
      });

      if (duplicateCheckResponse.data.data.length > 0 && !this.isEditing) {
        this.$bvToast.toast(
          'A tour with the same code, year, and cruise line already exists.',
          {
            title: 'Duplicate Entry',
            variant: 'danger',
            solid: true
          }
        );
        return;
      }

      // Build payload
      const payload = {
        data: {
          tourName: this.form.tourName,
          cruiseLine: this.form.cruiseLine,
          port: { id: this.selectedPort },
          tourCode: this.form.tourCode,
          year: this.form.year,
          cities: this.selectedCities.map(id => ({ id })),
          duration: this.form.duration,
          paxMin: this.form.paxMin,
          paxMax: this.form.paxMax,
          timing: this.form.timing,
          description: this.form.description,
          item: this.selectedItems.map(id => ({ id }))
        }
      };

      // Create or update
      if (this.isEditing) {
        await this.$xhr.put(`/api/tourlondons/${this.tour.id}`, payload);
      } else {
        await this.$xhr.post('/api/tourlondons', payload);
      }

      // Notify parent and close
      this.$emit('tour-added');
      this.handleClose();
    }


  }
};
</script>

<style scoped>
.modal-dialog-centered {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  max-width: 850px;
}

.tl-modal {
  border: none;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.tl-modal-header {
  background: #fff;
  border-bottom: 1px solid #f1f5f9;
  padding: 16px 24px;
}

.tl-modal-header .modal-title {
  font-size: 1.15rem;
  font-weight: 800;
  color: #1e293b;
}

.tl-modal-body {
  padding: 24px;
  background: #fff;
  max-height: 85vh;
  overflow-y: auto;
}

.tl-form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tl-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.tl-input,
.tl-select {
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #f9fafb;
  font-size: 0.82rem;
  color: #374151;
  transition: all 0.2s;
  outline: none;
}

.tl-textarea {
  height: auto;
  padding: 12px 16px;
}

.tl-input:focus,
.tl-select:focus {
  border-color: #E2923D;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, 0.15);
}

.tl-select-group {
  display: flex;
  gap: 12px;
  width: 100%;
}

.tl-btn-plus {
  width: 42px;
  height: 42px;
  background: #64748b;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}

.tl-btn-plus:hover {
  background: #475569;
}

.tl-label-sub {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 8px;
  letter-spacing: 0.05em;
}

.tl-selected-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tl-selected-item {
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e2e8f0;
}

.tl-remove-btn {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.1rem;
  padding: 0;
  line-height: 1;
  cursor: pointer;
}

.tl-remove-btn:hover {
  color: #ef4444;
}

.tl-form-actions {
  display: flex;
  justify-content: flex-end;
}

.tl-btn-submit {
  background: #E2923D;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0 24px;
  height: 34px;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  transition: opacity 0.2s;
}

.tl-btn-submit:hover {
  opacity: 0.9;
}

.tl-error-text {
  color: #ef4444;
  font-size: 0.85rem;
  margin-top: 8px;
  text-align: center;
}
</style>
